import React, { useState, useEffect, useCallback } from 'react';
import API_BASE from '../../utils/api.js';
import { authFetch } from '../utils/authFetch';
import notify from '../../utils/notify';
import '../../component-styles/SendEmailModal.css';

const DEFAULTS = {
  primaryColor: '#a5d27d',
  buttonTextColor: '#1a1a1a',
};

export default function SendEmailModal({
  type,           // 'invoice' | 'proposal'
  defaultEmail,
  clientName,
  docNumber,      // invoiceNumber or proposalTitle
  docUrl,
  companyName,
  projectId,      // needed to persist the processing-fee choice on the document
  documentId,     // invoiceId or proposalId
  feeEndpoint,    // overrides the type-based invoice/proposal fee endpoint below —
                   // needed for document types (e.g. time billing) that reuse this
                   // modal's email template but persist the fee flag elsewhere
  onGenerateTearSheet, // proposal-only: () => Promise<url>
  onClose,
  onSent,
}) {
  const userId = localStorage.getItem('userId');
  const typeLabel = type === 'invoice' ? 'Invoice' : 'Proposal';

  const [email, setEmail] = useState(defaultEmail || '');
  const [subject, setSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [primaryColor, setPrimaryColor] = useState(DEFAULTS.primaryColor);
  const [buttonTextColor, setButtonTextColor] = useState(DEFAULTS.buttonTextColor);
  const [signatureText, setSignatureText] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [defaultSubjectTemplate, setDefaultSubjectTemplate] = useState('');
  const [defaultInvoiceSubjectTemplate, setDefaultInvoiceSubjectTemplate] = useState('');
  const [attachTearSheet, setAttachTearSheet] = useState(false);
  const [tearSheetUrl, setTearSheetUrl] = useState('');
  const [preparingTearSheet, setPreparingTearSheet] = useState(false);
  const [chargeProcessingFee, setChargeProcessingFee] = useState(false);

  const [activeTab, setActiveTab] = useState('compose');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    async function loadBranding() {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${userId}`);
        const data = await res.json();
        setPrimaryColor(data.emailBranding?.primaryColor || DEFAULTS.primaryColor);
        setButtonTextColor(data.emailBranding?.buttonTextColor || DEFAULTS.buttonTextColor);
        setSignatureText(data.emailBranding?.signatureText || '');
        setLogoUrl(data.company?.logoUrl || '');
        setMessageTemplates(Array.isArray(data.messageTemplates) ? data.messageTemplates : []);
        setDefaultSubjectTemplate(data.proposalTemplate?.defaultEmailSubject || '');
        setDefaultInvoiceSubjectTemplate(data.invoiceTemplate?.defaultEmailSubject || '');
        setChargeProcessingFee(data.paymentFees?.passProcessingFeesToClient === true);
      } catch (err) {
        console.error('Failed to load email branding', err);
      }
    }
    if (userId) loadBranding();
  }, [userId]);

  const handleInsertTemplate = (e) => {
    const tpl = messageTemplates.find((t) => t.id === e.target.value);
    if (tpl) setCustomMessage(tpl.body);
    e.target.value = '';
  };

  const fetchPreview = useCallback(async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API_BASE}/api/notifications/preview-email`, {
        method: 'POST',
        body: JSON.stringify({
          type,
          clientName,
          docNumber,
          docUrl,
          companyName,
          customMessage,
          primaryColor,
          buttonTextColor,
          signatureText,
          logoUrl,
        }),
      });
      const data = await res.json();
      setPreviewHtml(data.html || '');
      if (!subject && data.subject) setSubject(data.subject);
      setActiveTab('preview');
    } catch (err) {
      console.error('Preview error', err);
      notify('Failed to load email preview.');
    } finally {
      setPreviewing(false);
    }
  }, [type, clientName, docNumber, docUrl, companyName, customMessage, primaryColor, buttonTextColor, signatureText, logoUrl, subject]);

  const handleAttachTearSheetToggle = async (checked) => {
    setAttachTearSheet(checked);
    if (!checked || tearSheetUrl || !onGenerateTearSheet) return;
    setPreparingTearSheet(true);
    try {
      const url = await onGenerateTearSheet();
      setTearSheetUrl(url || '');
    } catch (err) {
      console.error('Failed to prepare tear sheet', err);
      notify('Failed to prepare tear sheet. You can still send without it.');
      setAttachTearSheet(false);
    } finally {
      setPreparingTearSheet(false);
    }
  };

  const handleSend = async () => {
    if (!email) return;
    setSending(true);
    try {
      if (documentId && projectId && userId) {
        const resolvedFeeEndpoint = feeEndpoint || (type === 'invoice'
          ? `${API_BASE}/api/invoices/${userId}/${projectId}/${documentId}/processing-fee`
          : `${API_BASE}/api/proposals/${userId}/${projectId}/${documentId}/processing-fee`);
        try {
          await authFetch(resolvedFeeEndpoint, {
            method: 'PATCH',
            body: JSON.stringify({ chargeProcessingFee }),
          });
        } catch (feeErr) {
          console.error('Failed to save processing fee setting', feeErr);
        }
      }

      const endpoint = type === 'invoice' ? 'send-invoice' : 'send-proposal';
      const payload =
        type === 'invoice'
          ? {
              toAddress: email,
              clientName,
              invoiceNumber: docNumber,
              invoiceUrl: docUrl,
              companyName,
              subject,
              customMessage,
              primaryColor,
              buttonTextColor,
              signatureText,
              logoUrl,
            }
          : {
              toAddress: email,
              clientName,
              proposalTitle: docNumber,
              proposalUrl: docUrl,
              companyName,
              subject,
              customMessage,
              primaryColor,
              buttonTextColor,
              signatureText,
              logoUrl,
              tearSheetUrl: attachTearSheet ? tearSheetUrl : undefined,
            };

      const res = await authFetch(`${API_BASE}/api/notifications/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to send');
      }

      notify(`${typeLabel} sent to ${email}`);
      onSent?.();
      onClose();
    } catch (err) {
      console.error('Send email error', err);
      notify('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleOverlayClick = () => {
    if (!sending) onClose();
  };

  return (
    <div className="sem-overlay" onClick={handleOverlayClick}>
      <div className="sem-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sem-header">
          <h3>Send {typeLabel} to Client</h3>
          <button className="sem-close" onClick={onClose} disabled={sending} aria-label="Close">
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="sem-tabs">
          <button
            className={activeTab === 'compose' ? 'sem-tab active' : 'sem-tab'}
            onClick={() => setActiveTab('compose')}
          >
            Compose
          </button>
          <button
            className={activeTab === 'preview' ? 'sem-tab active' : 'sem-tab'}
            onClick={previewHtml ? () => setActiveTab('preview') : fetchPreview}
            disabled={previewing}
          >
            {previewing ? 'Loading…' : 'Preview'}
          </button>
        </div>

        {/* Body */}
        <div className="sem-body">
          {/* Compose Tab */}
          {activeTab === 'compose' && (
            <div className="sem-compose">
              <div className="sem-field">
                <label>To <span className="sem-optional">(separate multiple addresses with a comma)</span></label>
                <input
                  type="email"
                  multiple
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="client@example.com, spouse@example.com"
                  disabled={sending}
                />
              </div>

              <div className="sem-field">
                <label>Subject <span className="sem-optional">(leave blank for default)</span></label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={
                    type === 'invoice'
                      ? defaultInvoiceSubjectTemplate
                        ? defaultInvoiceSubjectTemplate
                            .replace(/\{companyName\}/g, companyName || 'your designer')
                            .replace(/\{invoiceNumber\}/g, docNumber || '')
                        : `Invoice #${docNumber || '…'} from ${companyName || 'your designer'}`
                      : defaultSubjectTemplate
                        ? defaultSubjectTemplate
                            .replace(/\{companyName\}/g, companyName || 'your designer')
                            .replace(/\{proposalNumber\}/g, docNumber || '')
                        : docNumber
                          ? `Your proposal "${docNumber}" is ready to review`
                          : 'Your proposal is ready to review'
                  }
                  disabled={sending}
                />
              </div>

              <div className="sem-field">
                <div className="sem-message-label-row">
                  <label>Message <span className="sem-optional">(leave blank for default)</span></label>
                  {messageTemplates.length > 0 && (
                    <select
                      className="sem-template-select"
                      defaultValue=""
                      onChange={handleInsertTemplate}
                      disabled={sending}
                    >
                      <option value="" disabled>Insert saved message…</option>
                      {messageTemplates.map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <textarea
                  rows={4}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder={
                    type === 'invoice'
                      ? `e.g. Hi! Please find your invoice attached. Let me know if you have any questions.`
                      : `e.g. Hi! I've put together a proposal for your project. Please take a look and let me know your thoughts.`
                  }
                  disabled={sending}
                />
              </div>

              {type === 'proposal' && onGenerateTearSheet && (
                <div className="sem-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={attachTearSheet}
                      disabled={sending || preparingTearSheet}
                      onChange={(e) => handleAttachTearSheetToggle(e.target.checked)}
                    />
                    Attach Tear Sheet
                    {preparingTearSheet && <span className="sem-optional">Preparing…</span>}
                  </label>
                </div>
              )}

              <div className="sem-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={chargeProcessingFee}
                    disabled={sending}
                    onChange={(e) => setChargeProcessingFee(e.target.checked)}
                  />
                  Charge client transaction fee
                </label>
                <p className="sem-optional" style={{ margin: '4px 0 0' }}>
                  Adds your configured card/ACH processing fee (Settings &gt; Payments) to whatever the client pays online for this {type}.
                </p>
              </div>

              <div className="sem-field">
                <label>Signature / Closing <span className="sem-optional">(leave blank to use saved default)</span></label>
                <textarea
                  rows={3}
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  placeholder={`e.g.\nBest regards,\nYour Studio Name`}
                  disabled={sending}
                />
              </div>

              <div className="sem-branding-section">
                <p className="sem-branding-title">Email Branding</p>
                <div className="sem-color-grid">
                  <div className="sem-color-field">
                    <label>Header &amp; Button Color</label>
                    <div className="sem-color-row">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="sem-color-swatch"
                      />
                      <input
                        type="text"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        maxLength={7}
                        className="sem-color-hex"
                      />
                    </div>
                  </div>
                  <div className="sem-color-field">
                    <label>Button Text Color</label>
                    <div className="sem-color-row">
                      <input
                        type="color"
                        value={buttonTextColor}
                        onChange={(e) => setButtonTextColor(e.target.value)}
                        className="sem-color-swatch"
                      />
                      <input
                        type="text"
                        value={buttonTextColor}
                        onChange={(e) => setButtonTextColor(e.target.value)}
                        maxLength={7}
                        className="sem-color-hex"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Preview Tab */}
          {activeTab === 'preview' && (
            <div className="sem-preview">
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="sem-preview-frame"
                  sandbox="allow-same-origin"
                />
              ) : (
                <p className="sem-preview-empty">Click "Preview" to see how the email will look.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sem-footer">
          <button className="if-btn-secondary" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button
            className="if-btn-ghost"
            onClick={fetchPreview}
            disabled={sending || previewing}
          >
            {previewing ? 'Loading…' : 'Refresh Preview'}
          </button>
          <button
            className="if-btn-primary"
            onClick={handleSend}
            disabled={!email || sending || preparingTearSheet}
          >
            {sending ? 'Sending…' : `Send ${typeLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
